import React from 'react';
import { Paper, Button, Typography, Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import FormField from '../Common/FormField';
import SkillsSection from '../Common/SkillsSection';
import StateDropdown from '../Common/StateDropdown';
import CityDropdown from '../Common/CityDropdown';
import { PersonalInfoFieldsProps, SkillEntry } from '../../types/profileForm.types';
import { UserInitialStateType } from 'DataTypes/Redux';
import { useSelector } from 'react-redux';
import ControlledForm from "Components/ControlledForm";

// Extended props type to include upload props if not already in types
interface ExtendedProps extends PersonalInfoFieldsProps {
  onFileChange?: (files: File[]) => void;
  onUpload?: () => void;
  files?: File[];
  isUploaded?: boolean;
}

const PersonalInfoFields: React.FC<ExtendedProps> = ({
  formData,
  errors,
  onChange,
  onBlur,
  onFileChange,
  onUpload,
  files = [],
  isUploaded = false,
}) => {
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );

  // Handle state change to clear city when state changes
  const handleStateChange = (name: string, value: string) => {
    // Clear city if state changes
    if (name === 'state' && value !== formData.state && formData.city) {
      onChange('city', '');
    }
    onChange(name, value);
  };

  // Handle skills section changes
  const handleSkillsChange = (skillEntries: SkillEntry[] | ((prev: SkillEntry[]) => SkillEntry[])) => {
    onChange('skillEntries', skillEntries);
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: '12px', mb: 2 }}>
      <Grid container columnSpacing={12} rowSpacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} >
          <FormField
            label="Name"
            name="name"
            type="text"
            value={formData.name}
            error={errors.name}
            placeholder="Enter your full name"
            required
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} >
          <FormField
            label="Email Address"
            name="email"
            type="email"

            value={formData.email}
            error={errors.email}
            placeholder="Enter your email address"
            required
            onChange={onChange}
            onBlur={onBlur}
            disabled
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }} >
          <FormField
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData.phone}
            error={errors.phone}
            placeholder="Enter your phone number"
            required
            inputProps={{ maxLength: 10 }}
            onChange={(name, value) => {
              if (/^\d*$/.test(value)) {
                onChange(name, value);
              }
            }}
            onBlur={onBlur}
            disabled={!(myInfo?.userType === 'NRIUSER' && String(myInfo?.phone) === '0000000000')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <FormField
            label="Date of Birth"
            name="dateOfBirth"
            type="date"
            value={formData.dateOfBirth}
            error={errors.dateOfBirth}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <FormField
            label="Address"
            name="address"
            type="text"
            value={formData.address}
            error={errors.address}
            placeholder="Enter your address"
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <StateDropdown
            label="State"
            value={formData.state || ''}
            error={errors.state}
            onChange={handleStateChange}
            onBlur={onBlur}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <CityDropdown
            label="City"
            value={formData.city || ''}
            selectedState={formData.state || ''}
            error={errors.city}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <FormField
            label="Postal Code"
            name="postalCode"
            type="text"
            value={formData.postalCode}
            error={errors.postalCode}
            placeholder="Enter your postal code"
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>


        {myInfo.userType === "CORPORATEUSER" && (
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <FormField
              label="Organisation Name"
              name="organisationName"
              type="text"
              value={formData.organisationName || ''}
              error={errors.organisationName}
              placeholder="Enter your organisation name"
              onChange={onChange}
              onBlur={onBlur}
              disabled
            />
          </Grid>
        )}

        {myInfo?.userType === 'PROFESSIONAL' && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="Highest Qualification"
                name="highestQualification"
                type="text"
                value={formData.highestQualification || ''}
                error={errors.highestQualification}
                placeholder="Enter your highest qualification"
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>

            {/* Document Upload Section for Professionals */}
            <Grid size={{ xs: 12, sm: 6 }} >
              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                Verification Documents
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} >
              <ControlledForm.Input
                id="fileSelectionProfile"
                type="file"
                onChange={(value) =>
                  onFileChange && onFileChange(typeof value == "object" ? value : [])
                }
                label="Upload Professional Qualification Documents"
                focused={true}
                readOnly={isUploaded}
                {...(isUploaded && {
                  borderColor: "success",
                  infoText: "Documents Uploaded Successfully",
                  iconId: "check-icon",
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} >
              <Button
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: isUploaded ? "#4CAF50" : undefined,
                  "&:hover": {
                    backgroundColor: isUploaded
                      ? "#43A047"
                      : undefined,
                  },
                  height: '56px'
                }}
                onClick={onUpload}
                disabled={isUploaded || (files && files.length === 0)}
              >
                {isUploaded ? "Uploaded" : "Upload"}
              </Button>
            </Grid>

            {/* Display Uploaded Certificates */}
            {formData.uploadedCertificates && formData.uploadedCertificates.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Existing Verified Documents
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {formData.uploadedCertificates.map((cert) => {
                    const baseUrl = process.env.REACT_APP_BASE_URL || '';
                    const fileUrl = cert.file.startsWith('http')
                      ? cert.file
                      : `${baseUrl}${cert.file.startsWith('/') ? '' : '/'}${cert.file}`;

                    return (
                      <Box key={cert.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                          Document Uploaded on {new Date(cert.uploadedAt).toLocaleDateString()}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(fileUrl, '_blank')}
                        >
                          View
                        </Button>
                      </Box>
                    )
                  })}
                </Box>
              </Grid>
            )}
            <Grid size={{ xs: 12 }} >
              <SkillsSection
                skillEntries={formData.skillEntries || []}
                errors={errors.skillEntries}
                onChange={handleSkillsChange}
                onBlur={onBlur}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }} >
              <FormField
                label="Description"
                name="description"
                type="textarea"
                value={formData.description}
                error={errors.description}
                placeholder="Write a short professional bio..."
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>

          </>
        )}
      </Grid>
    </Paper>
  );
};

export default PersonalInfoFields;